/**
 * Subscription Dashboard Types
 * Defines all TypeScript interfaces for subscriptions management
 */

// Subscription type and status enums
export type SubscriptionType = 'social-media' | 'legal-search' | 'database' | 'saas' | 'commercial-credit';
export type SubscriptionStatus = 'active' | 'inactive' | 'pending-setup';
export type PasswordAction = 'create' | 'read' | 'update' | 'delete';

/**
 * Core subscription model
 */
export interface Subscription {
  id: string;
  type: SubscriptionType;
  name: string;
  description: string;
  signInUrl: string;
  status: SubscriptionStatus;
  icon?: string;
  setupInstructions?: string;
  setupTime?: string;
  cost?: string;
  createdAt: Date;
  updatedAt: Date;
  categoryOrder?: number;
}

/**
 * Password metadata for a subscription
 * Links to AWS Secrets Manager secret
 */
export interface SubscriptionPassword {
  subscriptionId: string;
  secretArn: string;
  username?: string;
  secretName: string;
  lastRotated: Date;
  nextRotation: Date;
  rotationEnabled: boolean;
  rotationDays: number;
}

/**
 * Request payload for password CRUD operations
 */
export interface PasswordCrudRequest {
  subscriptionId: string;
  action: PasswordAction;
  payload: {
    username?: string;
    password?: string;
    metadata?: Record<string, string>;
  };
}

/**
 * Response from password CRUD operations
 * Note: passwords are never returned in plain text
 */
export interface PasswordResponse {
  success: boolean;
  message: string;
  data?: {
    username: string;
    passwordMasked: string; // Always "••••••••", never plaintext
    lastRotated: Date;
    nextRotation: Date;
  };
}

/**
 * Grouped subscriptions by type for accordion rendering
 */
export interface SubscriptionsByType {
  [key in SubscriptionType]: Subscription[];
}

/**
 * State for password edit form
 */
export interface PasswordEditState {
  isEditing: boolean;
  username: string;
  newPassword: string;
  confirmPassword: string;
  isLoading: boolean;
  error: string | null;
}

/**
 * State for individual subscription item
 */
export interface SubscriptionItemState {
  isExpanded: boolean;
  passwordState: PasswordEditState;
  passwordInfo: SubscriptionPassword | null;
  showPassword: boolean;
}

/**
 * Dashboard state
 */
export interface SubscriptionsDashboardState {
  subscriptions: Subscription[];
  subscriptionsByType: SubscriptionsByType;
  selectedSubscriptionId: string | null;
  isLoading: boolean;
  error: string | null;
  expandedAccordions: Set<SubscriptionType>;
  auditLog: AuditLogEntry[];
}

/**
 * Audit log entry for password operations
 */
export interface AuditLogEntry {
  id: string;
  subscriptionId: string;
  action: PasswordAction;
  userId: string;
  timestamp: Date;
  ipAddress?: string;
  userAgent?: string;
  details?: Record<string, unknown>;
  status: 'success' | 'failure';
  message?: string;
}

/**
 * Form validation errors
 */
export interface FormValidationError {
  field: string;
  message: string;
}

/**
 * API error response
 */
export interface ApiError {
  success: false;
  error: string;
  message: string;
  code: number;
  timestamp: string;
  details?: Record<string, unknown>;
}

/**
 * Success response wrapper
 */
export interface ApiSuccessResponse<T> {
  success: true;
  data: T;
  message?: string;
  timestamp: string;
}

/**
 * Component props
 */
export interface SubscriptionsDashboardProps {
  onSubscriptionUpdate?: (subscription: Subscription) => void;
  onPasswordChange?: (subscriptionId: string) => void;
  showAuditLog?: boolean;
  maxAuditEntries?: number;
}

export interface SubscriptionAccordionProps {
  type: SubscriptionType;
  subscriptions: Subscription[];
  isExpanded: boolean;
  onToggle: (type: SubscriptionType) => void;
}

export interface SubscriptionItemProps {
  subscription: Subscription;
  isExpanded: boolean;
  onToggle: (id: string) => void;
  onPasswordChange?: () => void;
}

export interface PasswordManagerProps {
  subscriptionId: string;
  username?: string;
  lastRotated?: Date;
  nextRotation?: Date;
  onPasswordUpdated?: () => void;
  disabled?: boolean;
}

export interface MaskToggleProps {
  isVisible: boolean;
  onToggle: () => void;
  maskedValue: string;
  actualValue?: string;
  label?: string;
}

/**
 * Subscription type metadata for display
 */
export interface SubscriptionTypeMetadata {
  type: SubscriptionType;
  label: string;
  description: string;
  icon: string;
  color?: string;
}

export const SUBSCRIPTION_TYPE_METADATA: Record<SubscriptionType, SubscriptionTypeMetadata> = {
  'social-media': {
    type: 'social-media',
    label: 'Social Media APIs',
    description: 'Access credentials for social media platforms',
    icon: '📱',
    color: '#1DA1F2'
  },
  'legal-search': {
    type: 'legal-search',
    label: 'Legal Search Tools',
    description: 'Access to legal databases and court records',
    icon: '⚖️',
    color: '#2E4053'
  },
  'database': {
    type: 'database',
    label: 'Database Services',
    description: 'Access credentials for data repositories',
    icon: '🗄️',
    color: '#E74C3C'
  },
  'saas': {
    type: 'saas',
    label: 'SaaS Tools',
    description: 'Third-party software and services',
    icon: '🔧',
    color: '#3498DB'
  },
  'commercial-credit': {
    type: 'commercial-credit',
    label: 'Commercial Credit',
    description: 'Enterprise credit, risk, and commercial data providers',
    icon: '🏢',
    color: '#5D4037'
  }
};
